/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-explicit-any */
// eslint-disable-next-line import/no-extraneous-dependencies
import { useMutation, useQuery } from "@tanstack/react-query";
import { client } from "./api-client";

export const useGetCashCenters = () =>
  useQuery<any[]>({
    queryKey: ["GetCashCenters"],
    queryFn: () => client(`lookups/cash/centers`),
  });

export const useGetCustomers = () =>
  useQuery<any[]>({
    queryKey: ["GetVaultCustomers"],
    queryFn: () => client(`lookups/customers`),
  });

export const useGetATMList = (customerId: string) =>
  useQuery<any>({
    queryKey: ["GetATMList", customerId],
    queryFn: () => client(`lookups/customer/atm/sites/${customerId}`),
    enabled: Boolean(customerId),
  });

export const useGetATMs = () =>
  useQuery<any[]>({
    queryKey: ["GetATMs"],
    queryFn: () => client(`lookups/atms`),
  });

export const useGetCDMs = () =>
  useQuery<any[]>({
    queryKey: ["GetCDMs"],
    queryFn: () => client(`lookups/cdms`),
  });

export const useGetKiosks = () =>
  useQuery<any[]>({
    queryKey: ["GetKiosks"],
    queryFn: () => client(`lookups/kiosks`),
  });

export const useGetVaultCurrencies = () =>
  useQuery<any[]>({
    queryKey: ["GetVaultCurrencies"],
    queryFn: () => client(`lookups/currencies`),
  });

export const useGetVaultDenominations = () =>
  useMutation({
    mutationFn: (shortName: string) =>
      client(`lookups/currency/denominations/${shortName}`),
  });

export const useGetCoinsDenominations = () =>
  useQuery({
    queryKey: ["GetCoinsDenom"],
    queryFn: () => client(`lookups/coins/denominations`),
  });

export const useGetVehicles = (cashCenterId: string) =>
  useQuery<any[]>({
    queryKey: ["GetVehicles", cashCenterId],
    queryFn: () => client(`lookups/cash/vans/${cashCenterId}`),
  });

export const useGetRouteTeams = () =>
  useQuery<any[]>({
    queryKey: ["GetRouteTeams"],
    queryFn: () => client(`lookups/all/routes`),
  });

export const useGetRoutes = () =>
  useQuery<any[]>({
    queryKey: ["GetRoutes"],
    queryFn: () => client(`lookups/all/routes`),
  });

export const useGetServicePoints = () =>
  useMutation({
    mutationFn: (customerId: string) =>
      client(`lookups/customer/cit/sites/${customerId}`),
  });

export const useGetCustomerServicePoints = (customerId?: string) =>
  useQuery({
    queryKey: ["GetCustomerServicePoints", customerId],
    queryFn: () => client(`lookups/customer/sites/${customerId}`),
    enabled: Boolean(customerId),
  });

export const useGetCustomerServicePointsBySource = (SSPId?: string) =>
  useQuery({
    queryKey: ["GetCustomerServicePoints", SSPId],
    queryFn: () => client(`lookups/locations/${SSPId}`),
    enabled: Boolean(SSPId),
  });

export const useAutocompleteAllLocations = (search: string) =>
  useQuery<any[]>({
    queryKey: ["AllLocations", search],
    queryFn: () =>
      client(`lookups/locations/autocomplete?searchString=${search}`),
    enabled: search.length >= 5,
  });

export const useGetShiftRoutes = (data: any) =>
  useQuery<any[]>({
    queryKey: [
      "GetRouteTeamLeaders",
      data?.operationDate,
      data?.cashCenterId,
      data?.shift,
    ],
    queryFn: () => client(`daily/operations/shift/routes`, { data }),
    enabled: Boolean(data?.cashCenterId),
  });

export const useGetDedicatedShiftRoutes = (data: any) =>
  useQuery<any[]>({
    queryKey: [
      "GetRouteTeamLeaders",
      data?.operationDate,
      data?.cashCenterId,
      data?.shift,
    ],
    queryFn: () => client(`daily/operations/shift/dedicated/routes`, { data }),
  });

export const useGetPendingReasons = () =>
  useQuery<any[]>({
    queryKey: ["GetPendingReasons"],
    queryFn: () => client(`lookups/pending/reasons/CIT`),
  });

export const useGetCurrenciesList = () =>
  useQuery({
    queryKey: ["GetCurrenciesList"],
    queryFn: () => client(`lookups/currencies`),
  });

export const useGetExchangeRate = () =>
  useMutation({
    mutationFn: (currency: string) =>
      client(`lookups/exchange/rate/${currency}`),
  });

export const useGetOffices = () =>
  useMutation({
    mutationFn: (cashCenterId: string) =>
      client(`lookups/cash/center/offices/${cashCenterId}`),
  });

export const useGetCities = (city: string) =>
  useQuery({
    queryKey: ["GetCities", city],
    queryFn: () => client(`lookups/cities/${city}`),
  });

export const useGetRegions = (state: string) =>
  useQuery({
    queryKey: ["GetRegions", state],
    queryFn: () => client(`lookups/regions/${state}`),
  });

export const useGetUsersByRole = (cashCenterId: string, role?: string) =>
  useQuery<any[]>({
    queryKey: ["GetUsersByRole", cashCenterId, role],
    queryFn: () => client(`auth/users/list/${cashCenterId}/${role}`),
    enabled: Boolean(role),
  });
export const useGetAllFiledTeams = (cashCenterId: string) =>
  useQuery<any[]>({
    queryKey: ["GetAllFieldTeams", cashCenterId],
    queryFn: () => client(`auth/field/team/${cashCenterId}`),
    enabled: Boolean(cashCenterId),
  });

export const useGetUsersByRole2 = (role?: string) =>
  useQuery<any[]>({
    queryKey: ["GetUsersByRole2", role],
    queryFn: () => client(`auth/users/list/${role}`),
    enabled: Boolean(role),
  });

export const useGetAllUsers = () =>
  useQuery<any[]>({
    queryKey: ["GetAllUsers"],
    queryFn: () => client(`auth/users/all`),
  });

export const useGetInRouteTeam = (data: any) =>
  useQuery<any[]>({
    queryKey: ["GetInRouteTeam", data?.cashCenterId, data?.operationDate],
    queryFn: () => client(`daily/operations/inroute/teams`, { data }),
    enabled: Boolean(data?.cashCenterId),
  });

export const useGetRouteByCashCenter = (cashCenterId: string) =>
  useQuery<any[]>({
    queryKey: ["GetRouteByCashCenter", cashCenterId],
    queryFn: () => client(`lookups/routes/${cashCenterId}`),
    enabled: Boolean(cashCenterId),
  });

export const useAutocompleteATMs = (search: string) =>
  useQuery({
    queryKey: ["AutocompleteATMs", search],
    queryFn: ({ signal }) =>
      client(`lookups/atms/autocomplete?searchString=${search}`, { signal }),
    enabled: search.length >= 5,
  });

export const useAutocompleteCDMs = (search: string) =>
  useQuery({
    queryKey: ["AutocompleteCDMs", search],
    queryFn: ({ signal }) =>
      client(`lookups/cdms/autocomplete?searchString=${search}`, { signal }),
    enabled: search.length >= 5,
  });

export const useAutocompleteKiosks = (search: string) =>
  useQuery({
    queryKey: ["AutocompleteKiosks", search],
    queryFn: ({ signal }) =>
      client(`lookups/kiosks/autocomplete?searchString=${search}`, { signal }),
    enabled: search.length >= 5,
  });

export const useGetCITList = () =>
  useQuery({
    queryKey: ["GetCITList"],
    queryFn: () => client(`lookups/cit/sites`),
  });

export const useGetCustomerCITList = (customerId: string) =>
  useQuery({
    queryKey: ["GetCustomerCITList", customerId],
    queryFn: () => client(`lookups/customer/cit/sites/${customerId}`),
    enabled: Boolean(customerId),
  });

export const useGetModelsList = (createType?: string) =>
  useQuery({
    queryKey: ["GetModelsList"],
    queryFn: () => client(`lookups/replenish/models`),
    enabled: createType ? createType === "multiple" : true,
  });

export const useGetKioskATMList = () =>
  useMutation({
    mutationFn: (customerId: string) =>
      client(`lookups/customer/kiosk/sites/${customerId}`),
  });

export const useGetCDMList = (customerId: string) =>
  useQuery<any[]>({
    queryKey: ["GetCDMList", customerId],
    queryFn: () => client(`lookups/customer/cdm/sites/${customerId}`),
  });

export const useGetCustomerATMList = () =>
  useMutation({
    mutationFn: (customerId: string) =>
      client(`lookups/customer/atm/sites/${customerId}`),
  });

export const useGetCashierLoads = (data: any) =>
  useQuery({
    queryKey: [
      "GetCashierLoads",
      data?.cashCenterId,
      data?.operationDate,
      data?.serviceType,
    ],
    queryFn: () => client(`statistics/processing/cashiers/load`, { data }),
  });

export const useGetDeliveryCashiersLoad = (data: any) =>
  useQuery({
    queryKey: [
      "GetDeliveryCashiersLoad",
      data?.operationDate,
      data?.serviceType,
    ],
    queryFn: () => client(`statistics/packing/cashiers/load`, { data }),
  });

export const useSendSupervisorOtp = (cashCenterId: string, email?: string) =>
  useMutation<
    any,
    Error,
    {
      to: string;
      siteArrivalTime: string;
    }
  >({
    mutationKey: ["SendSupervisorOtp", cashCenterId, email],
    mutationFn: (data) =>
      client(
        process.env.REACT_APP_CLIENT === "CT"
          ? `notifications/send/web/otp`
          : "notifications/generate/otp",
        process.env.REACT_APP_CLIENT === "CT" ? { data } : undefined,
      ),
  });

export const useGetSupervisors = (cashCenterId: string, role?: string) =>
  useQuery<any[]>({
    queryKey: ["GetSupervisors", cashCenterId, role],
    queryFn: () => client(`auth/users/supervisors/${cashCenterId}`),
    enabled: !role,
  });

export const useGetDiscrepancyTypes = () =>
  useQuery<any[]>({
    queryKey: ["GetSupervisors"],
    queryFn: () => client(`lookups/discrepancy/types`),
  });

export const useGetUsersByMultipleRoles = (data: string[]) =>
  useQuery<any[]>({
    queryKey: ["GetSupervisors", data],
    queryFn: () => client(`auth/users/in/roles`, { data }),
  });

export const useGetUsersByMultipleRoles2 = (data: string[]) =>
  useQuery<any[]>({
    queryKey: ["GetSupervisors", data],
    queryFn: () => client(`auth/users/in/roles/cc`, { data }),
  });

export const useGetThirdPartyRoutes = (
  cashCenterId?: string,
  vendorName?: string,
) =>
  useQuery<any[]>({
    queryKey: ["GetThirdPartyRoutes", cashCenterId, vendorName],
    queryFn: () => client(`lookups/tp/routes/${cashCenterId}/${vendorName}`),
    enabled: Boolean(cashCenterId) && Boolean(vendorName),
  });

export const useVerifySealNo = (cashCenterId: string) =>
  useMutation({
    mutationFn: (sealNo: string) =>
      client(`resources/consumables/verify/${cashCenterId}/${sealNo}`),
  });

export const useGetVaultAvailableRacks = (cashCenterId: string) =>
  useQuery<any>({
    queryKey: ["GetVaultAvailableRacks", cashCenterId],
    queryFn: () => client(`lookups/racks/${cashCenterId}`),
  });

export const useGetActivityTypes = () =>
  useQuery<any>({
    queryKey: ["GetActivityTypes"],
    queryFn: () => client(`lookups/activity/types`),
  });

export const useGetAssignmentGroups = () =>
  useQuery<any>({
    queryKey: ["GetAssignmentGroups"],
    queryFn: () => client(`lookups/assignment/groups`),
  });

export const useGetCategories = () =>
  useMutation({
    mutationFn: (activityType: string) =>
      client(`lookups/activity/categories/${activityType}`),
  });

export const useGetSubCategories = () =>
  useMutation({
    mutationFn: (data: { activityType: string; category: string }) =>
      client(
        `lookups/activity/sub/categories/${data.activityType}/${data.category}`,
      ),
  });

export const useGetImage = () => {
  const isLocal = process.env.REACT_APP_LOCAL_FILE_SYSTEM === "true";

  return useMutation({
    mutationFn: (path: string) =>
      client(
        isLocal
          ? `common/download/local?path=${path}`
          : `common/download/cloud?url=${path}`,
        {
          responseType: "blob",
        },
      ),
  });
};

export const useGetMachineServicePoints = () =>
  useMutation({
    mutationFn: (customerId: string) =>
      client(`lookups/customer/machine/sites/${customerId}`),
  });

export const useGetDispatchedRoutes = (data: any) =>
  useQuery<any>({
    queryKey: ["GetDispatchedRoutes", data?.cashCenterId, data?.operationDate],
    queryFn: () => client(`daily/operations/dispatched/routes`, { data }),
  });

export const useGetATMMachines = () =>
  useQuery<any[]>({
    queryKey: ["GetATMMachines"],
    queryFn: () => client(`lookups/atms`),
  });

export const useCheckRosterGenerated = () =>
  useMutation<boolean, Error, any>({
    mutationFn: (data) =>
      client(`daily/operations/route/roster/generated`, { data }),
  });

export const useAutocompleteCIT = (search: string) =>
  useQuery({
    queryKey: ["AutocompleteKiosks", search],
    queryFn: ({ signal }) =>
      client(`lookups/cit/sites/autocomplete?searchString=${search}`, {
        signal,
      }),
    enabled: search.length >= 5,
  });

export const useAutocompleteInterBranchCIT = (
  search: string,
  cashCenterId?: string,
) =>
  useQuery({
    queryKey: ["AutocompleteInterbranch", search, cashCenterId],
    queryFn: ({ signal }) =>
      client(
        `lookups/ib/sites/autocomplete?searchString=${search}&cashCenterId=${cashCenterId}`,
        {
          signal,
        },
      ),
    enabled: search.length >= 5 && Boolean(cashCenterId),
  });

export const useGetAllBagTypes = (
  type?:
    | "B2B"
    | "Delivery"
    | "Kiosk Service"
    | "Replenishment"
    | "Clearance"
    | "Collection"
    | "Exchange"
    | "InterBranch",
) => {
  let url = "";
  switch (type) {
    case "B2B":
      url = "lookups/bag/types/b2b";
      break;

    case "InterBranch":
      url = "lookups/bag/types/b2b";
      break;

    case "Delivery":
      url = "lookups/bag/types/delivery";
      break;

    case "Exchange":
      url = "lookups/bag/types/delivery";
      break;

    case "Kiosk Service":
      url = "lookups/bag/types/kiosk";
      break;

    case "Replenishment":
      url = "lookups/bag/types/atms";
      break;

    case "Clearance":
      url = "lookups/bag/types/clearance";
      break;

    case "Collection":
      url = "lookups/bag/types/collection";
      break;

    default:
      url = "lookups/bag/types";
      break;
  }
  return useQuery<any[]>({
    queryKey: ["GetAllBagTypes", type],
    queryFn: () => client(url),
  });
};

export const useGetCollectionBags = () =>
  useQuery<any[]>({
    queryKey: ["GetCollectionBags"],
    queryFn: () => client(`lookups/bag/types/collection`),
  });

export const useGetDeliveryBags = () =>
  useQuery<any[]>({
    queryKey: ["GetDeliveryBags"],
    queryFn: () => client(`lookups/bag/types/delivery`),
  });

export const useGetB2BBags = () =>
  useQuery<any[]>({
    queryKey: ["GetB2BBags"],
    queryFn: () => client(`lookups/bag/types/b2b`),
  });

export const useGetKioskList = (customerId: string) =>
  useQuery<any[]>({
    queryKey: ["GetKioskList", customerId],
    queryFn: () => client(`lookups/customer/kiosk/sites/${customerId}`),
    enabled: Boolean(customerId),
  });

export const useGetCdmList = () =>
  useQuery<any[]>({
    queryKey: ["GetCdmList"],
    queryFn: () => client(`lookups/cdm/list`),
  });

export const useAutocompleteHolding = (search: string) =>
  useQuery<any[]>({
    queryKey: ["AutocompleteHolding", search],
    queryFn: ({ signal }) =>
      client(`lookups/holding/locations/autocomplete?searchString=${search}`, {
        signal,
      }),
    enabled: search.length >= 5,
  });

export const useGetCoinContainers = () =>
  useQuery<any[]>({
    queryKey: ["GetCoinContainers"],
    queryFn: () => client(`lookups/coins/containers`),
  });

export const useGetContainersDiscrepancy = (category: string) =>
  useQuery<any[]>({
    queryKey: ["GetCoinContainers", category],
    queryFn: () => client(`lookups/package/discrepancy/reasons/${category}`),
  });

export const useGetDispatchedTeam = (data: any) =>
  useQuery<any[]>({
    queryKey: [
      "GetDispatchedTeam",
      data?.cashCenterId,
      data?.shift,
      data?.operationDate,
    ],
    queryFn: () => client(`daily/operations/dispatched/field/teams`, { data }),
  });

export const useGetServiceTypes = () =>
  useQuery<any[]>({
    queryKey: ["GetServiceTypes"],
    queryFn: () => client(`lookups/service/types`),
  });

export const useValidateBulkOrders = () =>
  useMutation({
    mutationFn: (data: any) =>
      client(`resources/sites/validate/barcodes`, {
        data,
        errorMessage: "Invalid barcodes found.",
      }),
  });
